import { useEffect, useRef, useState } from 'react';
import { cleanLook, type Look } from '../game/avatar';
import { drawBackground } from '../game/levels';
import { carById, CARS, drawCar, rideHeight, type CarId } from '../game/cars';
import { drawRaceItem, drawTerrain, groundAt, slopeAt, type RaceLevel } from '../game/race';
import { myCars } from '../lib/tower';
import { sfxBoing, sfxCoin, sfxHurt, sfxRare } from '../game/sfx';
import type { LevelResult } from './LevelPlayer';
import { useTouch } from './TouchControls';

// ─────────────────────────────────────────────────────────────────────────────
// Drives a race level (game/race.ts), Hill Climb Racing style. The car is a
// body with two wheels; each wheel pushes off the ground like a spring (that's
// the suspension) and drives along it. Gas (→ / D) speeds up; brake (← / A)
// slows down and then reverses. In the air, gas tips the car back and brake
// tips it forward. Landing on your head is a crash: back to the last
// checkpoint. Physics runs at 240 steps a second, in tiles.
// ─────────────────────────────────────────────────────────────────────────────

const STEP = 1 / 240;
const CAR_KEY = 'race.car';

type Props = {
  level: RaceLevel;
  look: unknown;
  title: string;
  onDone?: (r: LevelResult) => void;
  onClose: () => void;
  footer?: React.ReactNode;
};

export default function RacePlayer(p: Props) {
  const touch = useTouch();
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const keys = useRef({ gas: false, brake: false });
  const [hud, setHud] = useState({ coins: 0, crashes: 0, secs: 0, progress: 0, speed: 0 });
  const [msg, setMsg] = useState('');
  const [result, setResult] = useState<LevelResult | null>(null);
  const [round, setRound] = useState(0);
  const doneRef = useRef(p.onDone);
  doneRef.current = p.onDone;
  const quitRef = useRef<() => void>(() => {});
  // Which car: any the student has won (game/cars.ts); the Racer is everyone's.
  const [owned, setOwned] = useState<CarId[]>(['racer']);
  const [carId, setCarId] = useState<CarId>(() => { try { return carById(localStorage.getItem(CAR_KEY)).id; } catch { return 'racer'; } });
  useEffect(() => { let on = true; myCars().then(c => { if (on) setOwned(['racer', ...c.filter(x => x !== 'racer')]); }).catch(() => {}); return () => { on = false; }; }, []);
  const car_ = owned.includes(carId) ? carId : 'racer';
  const pickCar = (id: CarId) => { setCarId(id); try { localStorage.setItem(CAR_KEY, id); } catch { /* fine */ } setResult(null); setRound(r => r + 1); };

  useEffect(() => {
    const L = p.level, CAR = carById(car_);
    const look: Look = p.look ? cleanLook(p.look) : cleanLook({});
    const items = L.items.split('');
    const coinsTotal = items.filter(c => c === 'o').length;
    const finishAt = items.indexOf('G');

    const place = (x: number) => {
      const gy = groundAt(L, x) ?? L.h - 4;
      return { x, y: gy - rideHeight(CAR), vx: 0, vy: 0, angle: Math.atan(slopeAt(L, x)), w: 0 };
    };
    let check = 3;
    const car = { ...place(check), wheel: 0, comp: [0, 0], onGround: false };
    let coins = 0, crashes = 0, secs = 0, over = false, ready = 1.0, t = 0, crashT = 0;
    const boosted = new Map<number, number>();
    let msgTimer = 0;
    const say = (m: string) => { setMsg(m); msgTimer = 2; };

    const finish = (won: boolean, why?: LevelResult['why']) => {
      if (over) return;
      over = true;
      const r = { won, secs: Math.round(secs * 10) / 10, deaths: crashes, coins, coinsTotal, why };
      if (won) sfxRare();
      setResult(r);
      doneRef.current?.(r);
    };
    quitRef.current = () => finish(false, 'quit');

    const crash = () => {
      if (crashT > 0 || over) return;
      crashes++; sfxHurt(); say('💥 Crash! Back to your checkpoint');
      crashT = 0.9;
    };

    const step = (dt: number) => {
      t += dt;
      if (ready > 0) { ready -= dt; return; }
      if (over) return;
      secs += dt;
      if (L.time > 0 && secs >= L.time) { finish(false, 'time'); return; }
      if (msgTimer > 0) { msgTimer -= dt; if (msgTimer <= 0) setMsg(''); }
      if (crashT > 0) {
        crashT -= dt;
        if (crashT <= 0) Object.assign(car, place(check));
        return;
      }

      const k = keys.current;
      const cos = Math.cos(car.angle), sin = Math.sin(car.angle);
      const toWorld = (lx: number, ly: number): [number, number] => [car.x + lx * cos - ly * sin, car.y + lx * sin + ly * cos];
      let fx = 0, fy = CAR.grav * CAR.mass, torque = 0, touching = 0;

      CAR.wheels.forEach(([lx, ly], i) => {
        const [wx, wy] = toWorld(lx, ly);
        const gy = groundAt(L, wx);
        car.comp[i] = 0;
        if (gy == null) return;
        const sl = slopeAt(L, wx), len = Math.hypot(1, sl);
        const pen = (wy + CAR.wheelR - gy) / len;
        if (pen <= 0) return;
        touching++;
        car.comp[i] = Math.min(0.12, pen);
        const nx = sl / len, ny = -1 / len;             // the ground's normal (pointing up)
        const tx = 1 / len, ty = sl / len;              // along the ground (pointing forward)
        const rx = wx - car.x, ry = wy - car.y;
        const vpx = car.vx - car.w * ry, vpy = car.vy + car.w * rx;
        const vn = vpx * nx + vpy * ny, vt = vpx * tx + vpy * ty;
        const fn = Math.max(0, CAR.spring * pen - CAR.damp * vn);
        let ft: number;
        if (k.gas) ft = CAR.engine * Math.max(0, 1 - Math.max(0, vt) / CAR.top);
        else if (k.brake) ft = vt > 0.5 ? -CAR.brake : -CAR.reverse * Math.max(0, 1 + vt / 6);
        else ft = -vt * 0.6;                              // rolling to a stop
        ft = Math.max(-CAR.grip * fn, Math.min(CAR.grip * fn, ft));
        // The ground pushes up at the contact point. The engine pushes at the hub, but only a share of its
        // turning effect is kept: all of it makes the car pop wheelies and flip over at every start.
        const cx = rx - nx * CAR.wheelR, cy = ry - ny * CAR.wheelR;
        fx += fn * nx + ft * tx; fy += fn * ny + ft * ty;
        torque += cx * (fn * ny) - cy * (fn * nx);
        torque += (rx * (ft * ty) - ry * (ft * tx)) * 0.3;
        car.wheel += (vt * dt) / CAR.wheelR;
      });
      car.onGround = touching > 0;
      if (!touching) {
        if (k.gas) car.w -= CAR.airSpin * dt;               // tip back
        if (k.brake) car.w += CAR.airSpin * dt;             // tip forward
        if (k.gas) car.wheel += 30 * dt;
      }
      car.vx += (fx / CAR.mass) * dt; car.vy += (fy / CAR.mass) * dt;
      car.w += (torque / CAR.inertia) * dt;
      car.w *= 1 - (touching ? 2.5 : 0.6) * dt;
      car.x += car.vx * dt; car.y += car.vy * dt; car.angle += car.w * dt;
      if (car.x < 0.5) { car.x = 0.5; car.vx = Math.max(0, car.vx); }

      // Head on the ground, or into a pit: a crash.
      const [hx, hy] = toWorld(CAR.head[0], CAR.head[1]);
      const hg = groundAt(L, hx);
      if ((hg != null && hy > hg - 0.05) || car.y > L.h + 3) { crash(); return; }

      // Things along the course.
      for (let i = Math.max(0, Math.floor(car.x) - 1); i <= Math.min(L.w - 1, Math.floor(car.x) + 1); i++) {
        const ch = items[i]; if (ch === '.') continue;
        const gy = groundAt(L, i + 0.5) ?? L.h;
        const near = Math.abs(car.x - (i + 0.5)) < 1.1;
        if (ch === 'o' && near && Math.abs(car.y - (gy - 1.4)) < 1.3) { items[i] = '.'; coins++; sfxCoin(); }
        else if (ch === 'z' && near && car.onGround && (boosted.get(i) ?? -9) < secs - 1.5) {
          boosted.set(i, secs); sfxBoing(); say('⚡ Boost!');
          car.vx += Math.cos(car.angle) * 9; car.vy += Math.sin(car.angle) * 9;
        } else if (ch === 'h' && car.x > i + 0.5 && check < i) { check = i; sfxBoing(); say('✅ Checkpoint!'); }
        else if (ch === 'G' && car.x > i + 0.5) finish(true);
      }
    };

    // ── Drawing ───────────────────────────────────────────────────────────────
    const c = canvasRef.current!, g = c.getContext('2d')!;
    let camX = 0, camY = 0, W = 0, H = 0, s = 32;
    const fit = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      W = wrapRef.current?.clientWidth ?? 600;
      H = Math.max(220, Math.min(W * (W < 600 ? 0.95 : 0.56), window.innerHeight * (W < 600 ? 0.5 : 0.7), 560));
      s = H / 11;
      c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
      c.style.width = `${W}px`; c.style.height = `${H}px`;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    camX = car.x * s - W * 0.35; camY = car.y * s - H * 0.55;
    const ro = new ResizeObserver(fit);
    if (wrapRef.current) ro.observe(wrapRef.current);

    const draw = () => {
      camX += (car.x * s - W * 0.35 + car.vx * s * 0.15 - camX) * 0.1;
      camY += (car.y * s - H * 0.55 - camY) * 0.1;
      drawBackground(g, L.theme, W, H, camX, t);
      drawTerrain(g, L, s, camX, camY, W, H);
      const fx = Math.max(0, Math.floor(camX / s) - 2), lx = Math.min(L.w - 1, Math.ceil((camX + W) / s) + 2);
      for (let i = fx; i <= lx; i++) {
        const ch = items[i]; if (ch === '.') continue;
        const gy = groundAt(L, i + 0.5); if (gy == null) continue;
        drawRaceItem(g, ch, (i + 0.5) * s - camX, gy * s - camY, s, t, ch === 'h' && check === i);
      }
      if (!(crashT > 0 && Math.floor(t * 12) % 2)) {
        drawCar(g, look, CAR, { x: car.x * s - camX, y: car.y * s - camY, angle: car.angle, s, t, wheel: car.wheel, wheelsY: [CAR.wheels[0][1] - car.comp[0], CAR.wheels[1][1] - car.comp[1]], gas: keys.current.gas });
      }
      if (ready > 0) {
        g.fillStyle = 'rgba(0,0,0,0.25)'; g.fillRect(0, 0, W, H);
        g.fillStyle = '#fff'; g.font = `900 ${Math.round(H * 0.16)}px system-ui`; g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillText(ready > 0.5 ? 'Ready…' : 'Go!', W / 2, H / 2);
      }
    };

    if (import.meta.env.DEV) (window as unknown as { __race?: unknown }).__race = {
      run: (sec: number, press: Partial<typeof keys.current> = {}) => {
        Object.assign(keys.current, press);
        for (let n = 0; n < sec / STEP; n++) step(STEP);
        return { x: +car.x.toFixed(2), y: +car.y.toFixed(2), angle: +car.angle.toFixed(2), vx: +car.vx.toFixed(2), ground: car.onGround, crashes, coins, check, over };
      },
    };

    let raf = 0, last = performance.now(), acc = 0, hudT = 0, alive = true;
    const frame = (now: number) => {
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      acc += Math.min(0.1, (now - last) / 1000); last = now;
      while (acc >= STEP) { step(STEP); acc -= STEP; }
      draw();
      if (++hudT % 6 === 0) setHud({ coins, crashes, secs: Math.floor(secs), progress: Math.max(0, Math.min(1, car.x / Math.max(1, finishAt))), speed: Math.round(Math.hypot(car.vx, car.vy) * 4) });
    };
    raf = requestAnimationFrame(frame);

    const set = (e: KeyboardEvent, down: boolean) => {
      const k = e.key.toLowerCase();
      if (k === 'arrowright' || k === 'd' || k === 'arrowup' || k === 'w') keys.current.gas = down;
      else if (k === 'arrowleft' || k === 'a' || k === 'arrowdown' || k === 's') keys.current.brake = down;
      else return;
      e.preventDefault();
    };
    const kd = (e: KeyboardEvent) => set(e, true), ku = (e: KeyboardEvent) => set(e, false);
    window.addEventListener('keydown', kd); window.addEventListener('keyup', ku);
    return () => {
      alive = false; cancelAnimationFrame(raf); ro.disconnect();
      window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku);
      keys.current = { gas: false, brake: false };
    };
  }, [p.level, p.look, round, car_]);

  const hold = (k: 'gas' | 'brake') => ({
    onPointerDown: (e: React.PointerEvent) => { e.preventDefault(); (e.target as HTMLElement).setPointerCapture?.(e.pointerId); keys.current[k] = true; },
    onPointerUp: () => { keys.current[k] = false; },
    onPointerCancel: () => { keys.current[k] = false; },
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  });
  const coinsTotal = (p.level.items.match(/o/g) ?? []).length;

  return (
    <div className="level-player panel">
      <div className="panel-head">
        <h3 dir="auto">🏎️ {p.title}</h3>
        <button type="button" className="icon" aria-label="Close" onClick={p.onClose}>✕</button>
      </div>
      {owned.length > 1 && (
        <div className="car-pick" role="radiogroup" aria-label="Your car">
          {CARS.filter(c => owned.includes(c.id)).map(c => (
            <button key={c.id} type="button" role="radio" aria-checked={c.id === car_} className={c.id === car_ ? 'on' : ''} title={c.blurb} onClick={() => pickCar(c.id)}>{c.icon} {c.name}</button>
          ))}
        </div>
      )}
      <div className="level-stage" ref={wrapRef}>
        <canvas ref={canvasRef} aria-label="The race. Right arrow for gas, left arrow to brake." />
        <div className="level-hud">
          <span className="race-progress" aria-label={`${Math.round(hud.progress * 100)}% of the way`}><i style={{ width: `${hud.progress * 100}%` }} /></span>
          {coinsTotal > 0 && <span>🪙 {hud.coins}/{coinsTotal}</span>}
          <span>💥 {hud.crashes}</span>
          <span>⏱ {p.level.time ? Math.max(0, p.level.time - hud.secs) : hud.secs}s</span>
          <span>{hud.speed} km/h</span>
        </div>
        {msg && <div className="level-msg">{msg}</div>}
        {result && (
          <div className="level-end">
            <div className="panel">
              <h2>{result.won ? '🏁 You finished!' : result.why === 'time' ? '⏰ Out of time' : 'Race over'}</h2>
              <p>{result.secs}s · 💥 {result.deaths} crash{result.deaths === 1 ? '' : 'es'}{result.coinsTotal ? ` · 🪙 ${result.coins}/${result.coinsTotal}` : ''}</p>
              {p.footer}
              <div className="row" style={{ justifyContent: 'center', gap: 8 }}>
                {!p.onDone && <button type="button" onClick={() => { setResult(null); setRound(r => r + 1); }}>Race again</button>}
                <button type="button" className="primary" onClick={p.onClose}>Done</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {touch && !result && (
        <div className="level-pad race-pedals">
          <button type="button" className="brake" aria-label="Brake" {...hold('brake')}>BRAKE</button>
          <button type="button" className="gas" aria-label="Gas" {...hold('gas')}>GAS</button>
        </div>
      )}
      <div className="row small muted level-help">
        {!touch && <span><kbd>→</kbd> or <kbd>D</kbd> gas · <kbd>←</kbd> or <kbd>A</kbd> brake · in the air, gas tips you back and brake tips you forward</span>}
        <span className="spacer" />
        {!result && <button type="button" className="small" onClick={() => quitRef.current()}>Give up</button>}
      </div>
    </div>
  );
}
